// src/app/core/auth/auth.service.ts
import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AccessApiService } from './access-api.service';

export interface LoginResult {
  success: boolean;
  requiresPasswordChange: boolean;
  error?: string;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY = 'vendara_token';
  private readonly REMEMBER_KEY = 'vendara_remember';
  private readonly accessApi = inject(AccessApiService);

  private readonly _token = signal<string | null>(null);
  readonly token = this._token.asReadonly();

  private _useLocalStorage = false;

  private readonly _requiresPasswordChange = signal<boolean>(false);
  readonly requiresPasswordChange = this._requiresPasswordChange.asReadonly();

  private readonly _user = signal<{ userId: number; username: string; profileId: number } | null>(null);
  readonly user = this._user.asReadonly();

  readonly isAuthenticated = computed(() => this._token() !== null && !this._requiresPasswordChange());

  constructor() {
    this._initFromStorage();
  }

  loginAsync(username: string, password: string, rememberMe: boolean): Observable<LoginResult> {
    return this.accessApi.login(username, password).pipe(
      map(response => {
        if (response.status === 'Ok' && response.data?.token) {
          const data = response.data;
          this._storeToken(data.token, rememberMe);
          const payload = decodeJwtPayload(data.token);
          if (payload) {
            this._user.set({
              userId: Number(payload['userId']),
              username: String(payload['username'] ?? ''),
              profileId: Number(payload['profileId']),
            });
          }
          const requiresChange = !!data.requiresPasswordChange;
          this._requiresPasswordChange.set(requiresChange); // set flag before token so isAuthenticated is consistent from first tick
          this._token.set(data.token);
          return { success: true, requiresPasswordChange: requiresChange };
        }
        return { success: false, requiresPasswordChange: false, error: response.msg ?? 'Login failed' };
      }),
      catchError((err: HttpErrorResponse) => {
        const msg = err.error?.msg ?? 'Login failed';
        return of({ success: false, requiresPasswordChange: false, error: msg });
      })
    );
  }

  logoutAsync(): Observable<void> {
    return this.accessApi.logout().pipe(
      finalize(() => this._clearAuth()),
      map(() => void 0),
      catchError(() => of(void 0))
    );
  }

  completePasswordChange(fullToken: string): void {
    localStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    if (this._useLocalStorage) {
      localStorage.setItem(this.TOKEN_KEY, fullToken);
    } else {
      sessionStorage.setItem(this.TOKEN_KEY, fullToken);
    }
    this._token.set(fullToken);
    const payload = decodeJwtPayload(fullToken);
    if (payload) {
      this._user.set({
        userId: Number(payload['userId']),
        username: String(payload['username'] ?? ''),
        profileId: Number(payload['profileId']),
      });
    }
    this._requiresPasswordChange.set(false);
  }

  logout(): void {
    this._clearAuth();
  }

  private _initFromStorage(): void {
    // Remove legacy key if present
    localStorage.removeItem('vendara_auth');

    let token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) {
      token = sessionStorage.getItem(this.TOKEN_KEY);
    }
    if (!token) return;

    const payload = decodeJwtPayload(token);
    if (!payload) {
      localStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.TOKEN_KEY);
      return;
    }

    if (typeof payload['exp'] === 'number' && payload['exp'] * 1000 < Date.now()) {
      localStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.TOKEN_KEY);
      return;
    }

    this._useLocalStorage = localStorage.getItem(this.REMEMBER_KEY) === '1';
    this._token.set(token);
    this._user.set({
      userId: Number(payload['userId']),
      username: String(payload['username'] ?? ''),
      profileId: Number(payload['profileId']),
    });
    this._requiresPasswordChange.set(payload['pwdChange'] === true || payload['pwdChange'] === 'true');
  }

  private _storeToken(token: string, rememberMe: boolean): void {
    this._useLocalStorage = rememberMe;
    if (rememberMe) {
      localStorage.setItem(this.REMEMBER_KEY, '1');
      localStorage.setItem(this.TOKEN_KEY, token);
      sessionStorage.removeItem(this.TOKEN_KEY);
    } else {
      localStorage.removeItem(this.REMEMBER_KEY);
      sessionStorage.setItem(this.TOKEN_KEY, token);
      localStorage.removeItem(this.TOKEN_KEY);
    }
  }

  private _clearAuth(): void {
    this._token.set(null);
    this._user.set(null);
    this._requiresPasswordChange.set(false);
    this._useLocalStorage = false;
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REMEMBER_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
  }
}
